import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type { categoryColors } from "@plasma/data/categories";
import { timeConvert24hrTo12hr } from "@plasma/utils/time-convert";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery as useConvexQuery } from "convex/react";
import { Copy, Info, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { categoryCardColors } from "~/components/menus/category-card-colors";
import {
  AvailabilityTimesFields,
  type AvailabilityTimesForm,
} from "~/components/shared/availability-times-fields";
import { ColorSelector } from "~/components/shared/color-selector";
import { RadioPanels } from "~/components/shared/radio-panels";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

const createCategoryRef = api.categories.create;

const addToMenuRef = api.categories.addToMenu;

const getAllICanAddToAMenuRef = api.categories.getAllICanAddToAMenu;

const getDefaultBusinessHoursRef = api.menus.getDefaultBusinessHours;

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/categories/new"
)({
  component: CreateCategoryModal,
});

interface CategoryFormData extends AvailabilityTimesForm {
  name: string;
  color: (typeof categoryColors)[number];
  isCategoryLockEnabled: boolean;
  matchBusinessHours: boolean;
}

function CreateCategoryModal() {
  const { menuId } = Route.useParams();
  const navigate = useNavigate();
  const [isNewCategory, setIsNewCategory] = useState(true);

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/categories` });
  };

  const createCategoryMutation = useMutation({
    mutationFn: (data: {
      matchBusinessHours?: boolean;
      isCategoryLockEnabled?: boolean;
      name: string;
      color: string;
    }) => {
      const { availabilityTimes: _unused, ...dataToSend } =
        data as typeof data & {
          availabilityTimes?: unknown;
        };
      return convexClient.mutation(createCategoryRef, dataToSend);
    },
  });

  const addToMenuMutation = useMutation({
    mutationFn: ({
      menuId: targetMenuId,
      categoryId,
    }: {
      menuId: Id<"menus">;
      categoryId: Id<"categories">;
    }) => {
      return convexClient.mutation(addToMenuRef, {
        menuId: targetMenuId,
        categoryId,
      });
    },
  });

  const handleCreateCategory = async (data: {
    matchBusinessHours?: boolean;
    isCategoryLockEnabled?: boolean;
    name: string;
    color: string;
  }) => {
    if (createCategoryMutation.isPending || addToMenuMutation.isPending) {
      return;
    }
    try {
      const categoryId = await createCategoryMutation.mutateAsync(data);
      // Track category creation in PostHog
      ProductEvents.categoryCreated(categoryId);
      await addToMenuMutation.mutateAsync({
        menuId: toConvexTableId("menus", menuId),
        categoryId,
      });
      handleClose();
    } catch (error) {
      logger.exception("category_creation_failed", error);
    }
  };

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent noScrollPrevention>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>Add a category</DialogTitle>
          <DialogDescription className="body">
            Use categories to group your menu items
          </DialogDescription>
        </DialogHeader>
        <RadioPanels
          items={[
            {
              value: "true",
              label: "New category",
              icon: Plus,
            },
            {
              value: "false",
              label: "Add existing category",
              icon: Copy,
            },
          ]}
          onValueChange={(val) => setIsNewCategory(val === "true")}
          value={isNewCategory.toString()}
        />
        {isNewCategory ? (
          <AddNewCategoryForm menuId={menuId} onSubmit={handleCreateCategory} />
        ) : (
          <AddExistingCategoryForm menuId={menuId} onFinished={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface AddExistingCategoryFormData {
  category: string;
}

function AddExistingCategoryForm({
  onFinished,
  menuId,
}: {
  onFinished: () => void;
  menuId: string;
}) {
  const categories =
    useConvexQuery(getAllICanAddToAMenuRef, {
      menuId: toConvexTableId("menus", menuId),
    }) ?? [];

  const addExistingCategoryMutation = useMutation({
    mutationFn: ({
      menuId: targetMenuId,
      categoryId,
    }: {
      menuId: Id<"menus">;
      categoryId: Id<"categories">;
    }) => {
      return convexClient.mutation(addToMenuRef, {
        menuId: targetMenuId,
        categoryId,
      });
    },
    onSuccess: () => {
      onFinished();
    },
    onError: (error) => {
      logger.exception("category_add_to_menu_failed", error);
    },
  });

  const form = useForm<AddExistingCategoryFormData>();

  const handleSubmit = async (data: AddExistingCategoryFormData) => {
    if (addExistingCategoryMutation.isPending) {
      return;
    }
    await addExistingCategoryMutation.mutateAsync({
      menuId: toConvexTableId("menus", menuId),
      categoryId: toConvexTableId("categories", data.category),
    });
  };

  if (categories.length === 0) {
    return <p>No categories available...</p>;
  }

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Choose existing category</FormLabel>
              <FormControl>
                <Combobox
                  dropdownClassName="w-130"
                  items={categories.map((category) => ({
                    label: category.name,
                    value: category.id,
                    keywords: [category.name],
                  }))}
                  onValueChange={field.onChange}
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button className="w-full" size="lg" type="submit">
          Add Category
        </Button>
      </form>
    </Form>
  );
}

function AddNewCategoryForm({
  onSubmit,
  menuId,
}: {
  onSubmit: (data: CategoryFormData) => void;
  menuId: string;
}) {
  const businessHours = useConvexQuery(getDefaultBusinessHoursRef, {
    menuId: toConvexTableId("menus", menuId),
  });

  const form = useForm<CategoryFormData>({
    defaultValues: {
      name: "",
      color: "gray",
      matchBusinessHours: true,
      availabilityTimes: [],
      isCategoryLockEnabled: false,
    },
  });

  useEffect(() => {
    if (businessHours) {
      form.setValue(
        "availabilityTimes",
        businessHours
          .map((bh) =>
            bh.isOpen && bh.openTime && bh.closeTime
              ? {
                  startTime: timeConvert24hrTo12hr(bh.openTime),
                  endTime: timeConvert24hrTo12hr(bh.closeTime),
                  dayOfTheWeek: bh.dayOfTheWeek,
                }
              : null
          )
          .filter((item): item is NonNullable<typeof item> => item !== null)
      );
    }
  }, [businessHours, form]);

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Category name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="color"
            render={({ field }) => (
              <ColorSelector onValueChange={field.onChange}>
                <div
                  className={twJoin(
                    "grid h-40 w-40 shrink-0 cursor-pointer place-items-center overflow-hidden break-words rounded-2xl p-4 text-center",
                    categoryCardColors[field.value ?? "white"]
                  )}
                >
                  <div className="flex flex-col space-y-1">
                    <p className="h4 text-foreground">{form.watch("name")}</p>
                    <p className="caption text-primary text-center">
                      Edit display colour
                    </p>
                  </div>
                </div>
              </ColorSelector>
            )}
          />
        </div>
        <fieldset>
          <Label asChild>
            <legend className="text-foreground flex items-center gap-1">
              When is this category available to order?
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="cursor-help">
                    <Info className="size-4" />
                    <span className="sr-only">
                      Hover for additional information
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Restrict or indicate to staff <br />
                      when menu items in this
                      <br />
                      category are available to order
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </legend>
          </Label>
          <FormField
            control={form.control}
            name="matchBusinessHours"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center pt-3">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="body-sm text-foreground">
                  Match location business hours
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
        </fieldset>
        {!form.watch("matchBusinessHours") && (
          <>
            <fieldset>
              <legend className="pb-3">
                <Label asChild>
                  <p>Set available hours</p>
                </Label>
                <p className="body-sm text-foreground">
                  Indicate when menu items are available in POS.
                </p>
              </legend>
              <AvailabilityTimesFields showLabel={false} />
            </fieldset>
            <fieldset>
              <Label asChild>
                <legend>Disable ordering outside of available hours</legend>
              </Label>
              <FormField
                control={form.control}
                name="isCategoryLockEnabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center pt-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="body-sm text-foreground">
                      Lock menu items when this category is not available
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>
          </>
        )}
        <Button className="w-full" size="lg" type="submit">
          Create
        </Button>
      </form>
    </Form>
  );
}
